import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowClockwise, ArrowSquareOut, Buildings, Camera, Check, CheckSquare, FileArrowDown, FilePdf, House, Images, Lightning, Link as LinkIcon, PencilSimple, Square, Trash, X } from "@phosphor-icons/react";
import { api, adminHeaders, fileUrl, getSecret, clearSecret } from "@/lib/api";
import { compressImage } from "@/lib/compress";
import { Lightbox } from "@/components/Lightbox";

function DriveButton({ href, icon: Icon, title, subtitle, testId }) {
    return (
        <a
            data-testid={testId}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-4 border border-border bg-white p-5 hover:border-primary hover:-translate-y-[1px] transition-all duration-200"
        >
            <Icon size={28} weight="light" className="text-primary shrink-0" />
            <span className="flex-1 min-w-0">
                <span className="block font-medium text-sm sm:text-base">{title}</span>
                <span className="block text-xs text-muted-foreground mt-0.5">{subtitle}</span>
            </span>
            <ArrowSquareOut size={18} weight="light" className="text-muted-foreground shrink-0" />
        </a>
    );
}

function ServicePhoto({ photo, isAdmin, canAfter, canSelect, canEdit, editingNote, editNoteValue, onEditNoteChange, onEditNoteStart, onEditNoteSave, selected, onSelect, onToggle, onDelete, onAfter, onZoom, index }) {
    const afterInputId = `after-input-${photo.id}`;
    return (
        <div
            className={`border bg-white fade-up ${selected ? "border-primary" : "border-border"}`}
            style={{ animationDelay: `${index * 60}ms` }}
            data-testid={`service-photo-${photo.id}`}
        >
            <div className="flex">
                <button
                    className="relative block flex-1 min-w-0 aspect-square overflow-hidden bg-secondary cursor-zoom-in"
                    onClick={() => onZoom(photo, "before")}
                    data-testid={`service-photo-zoom-${photo.id}`}
                    aria-label="Perbesar foto before"
                >
                    <img
                        src={fileUrl(photo.storage_path)}
                        alt={photo.note || "Foto service"}
                        className={`w-full h-full object-cover transition-all duration-300 ${photo.is_done ? "grayscale" : ""}`}
                        loading="lazy"
                    />
                    <span className="absolute bottom-1 left-1 bg-stone-950/70 text-white text-[9px] tracking-widest px-1.5 py-0.5">BEFORE</span>
                    {photo.is_done && (
                        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <span className="bg-stone-950/70 text-white text-[10px] font-bold tracking-[0.25em] px-3 py-1.5 -rotate-6" data-testid={`service-photo-done-overlay-${photo.id}`}>
                                SUDAH SELESAI
                            </span>
                        </span>
                    )}
                </button>
                {photo.after_path && (
                    <button
                        className="relative block flex-1 min-w-0 aspect-square overflow-hidden bg-secondary cursor-zoom-in border-l border-white"
                        onClick={() => onZoom(photo, "after")}
                        data-testid={`service-photo-after-zoom-${photo.id}`}
                        aria-label="Perbesar foto after"
                    >
                        <img
                            src={fileUrl(photo.after_path)}
                            alt={`After ${photo.note || "foto service"}`}
                            className={`w-full h-full object-cover transition-all duration-300 ${photo.is_done ? "grayscale" : ""}`}
                            loading="lazy"
                        />
                        <span className="absolute bottom-1 left-1 bg-primary/90 text-white text-[9px] tracking-widest px-1.5 py-0.5">AFTER</span>
                        {photo.is_done && (
                            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <span className="bg-stone-950/70 text-white text-[10px] font-bold tracking-[0.25em] px-3 py-1.5 -rotate-6" data-testid={`service-photo-done-overlay-after-${photo.id}`}>
                                    SUDAH SELESAI
                                </span>
                            </span>
                        )}
                    </button>
                )}
            </div>
            <div className="p-3 flex items-center gap-2">
                {isAdmin && (
                    <button
                        data-testid={`service-photo-toggle-${photo.id}`}
                        onClick={() => onToggle(photo)}
                        className="w-10 h-10 -my-2 -ml-2 shrink-0 flex items-center justify-center"
                        aria-label="Tandai selesai"
                    >
                        <span
                            className={`w-6 h-6 border flex items-center justify-center transition-colors duration-200 ${
                                photo.is_done ? "bg-primary border-primary text-primary-foreground" : "border-border bg-white"
                            }`}
                        >
                            {photo.is_done && <Check size={14} weight="bold" />}
                        </span>
                    </button>
                )}
                <div className="flex-1 min-w-0">
                    {editingNote ? (
                        <div className="flex gap-1">
                            <input
                                data-testid={`service-photo-note-edit-${photo.id}`}
                                value={editNoteValue}
                                onChange={(e) => onEditNoteChange(e.target.value)}
                                placeholder="Catatan"
                                className="flex-1 min-w-0 border border-border px-2 py-1 text-xs outline-none focus:border-primary bg-transparent"
                            />
                            <button
                                data-testid={`service-photo-note-save-${photo.id}`}
                                onClick={() => onEditNoteSave(photo)}
                                className="shrink-0 border border-primary text-primary px-2 hover:bg-primary hover:text-primary-foreground transition-colors"
                                title="Simpan catatan"
                            >
                                <Check size={13} weight="bold" />
                            </button>
                        </div>
                    ) : (
                        <>
                            <p className={`text-xs truncate ${photo.is_done ? "line-through text-muted-foreground" : ""}`} data-testid={`service-photo-note-${photo.id}`}>
                                {photo.ref && <span className="font-mono font-medium">{photo.ref} — </span>}
                                {photo.note || "Foto service"}
                            </p>
                            <p className="text-[10px] text-muted-foreground font-mono">{new Date(photo.created_at).toLocaleDateString("id-ID")}</p>
                        </>
                    )}
                </div>
                {(isAdmin || canAfter || canSelect || canEdit) && (
                    <div className="flex items-center gap-1 shrink-0">
                        {canEdit && !editingNote && (
                            <button
                                data-testid={`service-photo-edit-${photo.id}`}
                                onClick={() => onEditNoteStart(photo)}
                                className="text-muted-foreground hover:text-primary transition-colors p-1"
                                title="Edit catatan"
                            >
                                <PencilSimple size={16} weight="light" />
                            </button>
                        )}
                        {canAfter && (
                            <>
                                <input
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    className="hidden"
                                    id={`${afterInputId}-camera`}
                                    onChange={(e) => onAfter(photo, e)}
                                    data-testid={`service-photo-after-camera-input-${photo.id}`}
                                />
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    id={`${afterInputId}-gallery`}
                                    onChange={(e) => onAfter(photo, e)}
                                    data-testid={`service-photo-after-gallery-input-${photo.id}`}
                                />
                                <button
                                    data-testid={`service-photo-after-camera-btn-${photo.id}`}
                                    onClick={() => document.getElementById(`${afterInputId}-camera`)?.click()}
                                    className="text-muted-foreground hover:text-primary transition-colors p-1"
                                    title={photo.after_path ? "Ganti foto after — dari kamera" : "Foto after langsung dari kamera"}
                                >
                                    <Camera size={16} weight="light" />
                                </button>
                                <button
                                    data-testid={`service-photo-after-gallery-btn-${photo.id}`}
                                    onClick={() => document.getElementById(`${afterInputId}-gallery`)?.click()}
                                    className="text-muted-foreground hover:text-primary transition-colors p-1"
                                    title={photo.after_path ? "Ganti foto after — dari galeri" : "Upload foto after dari galeri"}
                                >
                                    <Images size={16} weight="light" />
                                </button>
                            </>
                        )}
                        {canSelect && (
                            <button
                                data-testid={`service-photo-select-${photo.id}`}
                                onClick={() => onSelect(photo.id)}
                                className={`p-1 transition-colors ${selected ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
                                title="Pilih untuk report"
                            >
                                {selected ? <CheckSquare size={16} weight="fill" /> : <Square size={16} weight="light" />}
                            </button>
                        )}
                        {isAdmin && (
                            <button
                                data-testid={`service-photo-delete-${photo.id}`}
                                onClick={() => onDelete(photo)}
                                className="text-muted-foreground hover:text-destructive transition-colors p-1"
                                title="Hapus foto"
                            >
                                <Trash size={16} weight="light" />
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function RoomPage({ mode = "public" }) {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const [room, setRoom] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [note, setNote] = useState("");
    const [zoomPhoto, setZoomPhoto] = useState(null);
    const [zoomIdx, setZoomIdx] = useState(null);
    const [adminVerified, setAdminVerified] = useState(mode !== "admin");
    const [selectedIds, setSelectedIds] = useState([]);
    const [generating, setGenerating] = useState(false);
    const fileRef = useRef(null);
    const galleryRef = useRef(null);
    const isAdmin = mode === "admin" && adminVerified;

    useEffect(() => {
        if (mode !== "admin") return;
        const saved = getSecret();
        if (!saved) {
            navigate("/admin");
            return;
        }
        api.post("/admin/verify", { phrase: saved })
            .then(() => setAdminVerified(true))
            .catch(() => {
                clearSecret();
                navigate("/admin");
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const load = async () => {
        try {
            const res = await api.get(`/rooms/${roomId}`);
            setRoom(res.data);
        } catch {
            setNotFound(true);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId]);

    const uploadPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const compressed = await compressImage(file);
            const form = new FormData();
            form.append("file", compressed, "service.jpg");
            form.append("note", note);
            form.append("source", mode === "public" ? "open" : "team");
            await api.post(`/rooms/${roomId}/service-photos`, form);
            setNote("");
            toast.success("Foto service terunggah");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal mengunggah foto");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const togglePhoto = async (photo) => {
        try {
            await api.patch(`/admin/service-photos/${photo.id}`, { is_done: !photo.is_done }, { headers: adminHeaders() });
            toast.success(!photo.is_done ? `${photo.ref || "Foto"} ditandai selesai` : `${photo.ref || "Foto"} dibuka kembali`);
            await load();
        } catch {
            toast.error("Gagal mengubah status");
        }
    };

    const uploadAfter = async (photo, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const compressed = await compressImage(file);
            const form = new FormData();
            form.append("file", compressed, "after.jpg");
            await api.post(`/service-photos/${photo.id}/after`, form);
            toast.success(photo.after_path ? "Foto after diganti" : "Foto after terunggah");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal mengunggah foto after");
        } finally {
            e.target.value = "";
        }
    };

    const toggleSelect = (id) =>
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    const generateReport = async () => {
        if (selectedIds.length === 0) {
            toast.error("Pilih minimal satu foto untuk report");
            return;
        }
        setGenerating(true);
        try {
            const res = await api.post(`/admin/rooms/${roomId}/report`, { photo_ids: selectedIds }, { headers: adminHeaders() });
            window.location.href = fileUrl(res.data.path);
            toast.success("Report PDF berhasil dibuat");
        } catch {
            toast.error("Gagal membuat report");
        } finally {
            setGenerating(false);
        }
    };

    const generateServiceReport = async (onlyPending) => {
        if (!onlyPending && selectedIds.length === 0) {
            toast.error("Pilih minimal satu foto untuk di-print");
            return;
        }
        setGenerating(true);
        try {
            const res = await api.post(
                `/rooms/${roomId}/report-service`,
                onlyPending ? { only_pending: true } : { photo_ids: selectedIds }
            );
            window.location.href = fileUrl(res.data.path);
            toast.success("PDF foto service berhasil dibuat");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Gagal membuat PDF");
        } finally {
            setGenerating(false);
        }
    };

    const deletePhoto = async (photo) => {
        if (!window.confirm("Hapus foto ini?")) return;
        try {
            await api.delete(`/admin/service-photos/${photo.id}`, { headers: adminHeaders() });
            toast.success("Foto dihapus");
            await load();
        } catch {
            toast.error("Gagal menghapus foto");
        }
    };

    const [editSvcId, setEditSvcId] = useState("");
    const [editSvcNote, setEditSvcNote] = useState("");

    const saveServiceNote = async (photo) => {
        try {
            await api.patch(`/service-photos/${photo.id}/note`, { note: editSvcNote });
            setEditSvcId("");
            toast.success("Catatan disimpan");
            await load();
        } catch {
            toast.error("Gagal menyimpan catatan");
        }
    };

    const [progressCount, setProgressCount] = useState(0);
    const [pullDist, setPullDist] = useState(0);
    const [refreshing, setRefreshing] = useState(false);
    const pullStart = useRef(null);

    const loadProgressCount = async () => {
        if (mode === "public") return;
        try {
            const res = await api.get(`/rooms/${roomId}/progress`, { params: { date_limit: 1 } });
            setProgressCount(res.data.total_photos);
        } catch {
            /* abaikan */
        }
    };

    useEffect(() => {
        loadProgressCount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId, mode]);

    const reloadAll = async () => {
        await load();
        await loadProgressCount();
    };

    const onPullStart = (e) => {
        if (window.scrollY <= 0) pullStart.current = e.touches[0].clientY;
    };
    const onPullMove = (e) => {
        if (pullStart.current == null) return;
        const dy = e.touches[0].clientY - pullStart.current;
        if (dy > 0 && window.scrollY <= 0) setPullDist(Math.min(dy * 0.4, 90));
    };
    const onPullEnd = async () => {
        if (pullDist > 60 && !refreshing) {
            setRefreshing(true);
            await reloadAll();
            setRefreshing(false);
        }
        pullStart.current = null;
        setPullDist(0);
    };

    if (mode === "admin" && !adminVerified) return null;

    if (notFound) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" data-testid="room-not-found">
                <img src="/logo.png" alt="INERRE interior" className="h-14 object-contain mb-8" />
                <h1 className="font-serif text-4xl tracking-tighter font-medium">Ruangan tidak ditemukan</h1>
                <p className="text-sm text-muted-foreground mt-3">QR code mungkin sudah tidak berlaku. Hubungi admin INERRE.</p>
            </div>
        );
    }

    if (!room) {
        return (
            <div className="min-h-screen flex items-center justify-center" data-testid="room-loading">
                <p className="text-sm text-muted-foreground">Memuat...</p>
            </div>
        );
    }

    const openPhotos = room.service_photos.filter((p) => (p.source || "open") === "open");
    const teamPhotos = room.service_photos.filter((p) => p.source === "team");
    const visiblePhotos = mode === "public" ? openPhotos : room.service_photos;
    const pendingCount = visiblePhotos.filter((p) => !p.is_done).length;
    const photoGroups =
        mode === "public"
            ? [{ key: "open", title: "Laporan yang Telah Disubmit", photos: openPhotos }]
            : [
                  { key: "open", title: "Laporan yang Telah Disubmit", photos: openPhotos },
                  { key: "team", title: "Foto Perbaikan (Supervisor & Admin)", photos: teamPhotos },
              ];

    const galleryImages = photoGroups.flatMap((g) =>
        [...g.photos]
            .sort((a, b) => Number(a.is_done) - Number(b.is_done) || new Date(b.created_at) - new Date(a.created_at))
            .flatMap((p) => {
                const items = [{ src: fileUrl(p.storage_path), ref: p.ref, note: p.note, isDone: p.is_done, isAfter: false }];
                if (p.after_path) items.push({ src: fileUrl(p.after_path), ref: p.ref, note: p.note, isDone: p.is_done, isAfter: true });
                return items;
            })
    );

    return (
        <div
            className="min-h-screen"
            data-testid="room-page"
            style={{ overscrollBehaviorY: "contain" }}
            onTouchStart={onPullStart}
            onTouchMove={onPullMove}
            onTouchEnd={onPullEnd}
        >
            {(pullDist > 0 || refreshing) && (
                <div
                    className="fixed top-0 inset-x-0 z-50 flex justify-center pointer-events-none"
                    data-testid="pull-refresh-indicator"
                    style={{ transform: `translateY(${refreshing ? 14 : Math.min(pullDist, 90) * 0.55}px)` }}
                >
                    <div className="bg-white border border-border shadow-sm px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2">
                        <ArrowClockwise size={13} weight="bold" className={refreshing ? "animate-spin" : ""} />
                        {refreshing ? "Memuat ulang..." : pullDist > 60 ? "Lepaskan untuk refresh" : "Tarik untuk refresh"}
                    </div>
                </div>
            )}
            <header className="sticky top-0 z-10 bg-white/80 backdrop-blur-xl border-b border-stone-200">
                <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
                    <img src="/logo.png" alt="INERRE interior" className="h-9 object-contain" data-testid="room-logo" />
                    <div className="min-w-0">
                        <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground truncate" data-testid="room-property-name">
                            {room.property_name}
                        </p>
                        <h1 className="font-serif text-2xl sm:text-3xl tracking-tight font-medium leading-tight truncate" data-testid="room-name">
                            {room.name}
                        </h1>
                    </div>
                    {mode !== "public" && (
                        <div className="ml-auto shrink-0 flex items-center gap-2">
                            <button
                                data-testid="room-refresh-btn"
                                onClick={() => window.location.reload()}
                                className="border border-border p-2 text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                                title="Muat ulang halaman"
                            >
                                <ArrowClockwise size={18} weight="light" />
                            </button>
                            <button
                                data-testid="room-list-btn"
                                onClick={() => navigate(`/${mode}/browse?prop=${room.property_id}`)}
                                className="border border-border p-2 text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                                title="Daftar ruangan proyek ini"
                            >
                                <Buildings size={18} weight="light" />
                            </button>
                            <button
                                data-testid="scan-again-btn"
                                onClick={() => navigate(mode === "admin" ? "/admin" : "/supervisor")}
                                className="border border-border p-2 text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                                title="Pindai QR lain"
                            >
                                <House size={18} weight="light" />
                            </button>
                        </div>
                    )}
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-8 sm:py-12 space-y-12">
                {mode !== "public" && (
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-primary" data-testid="mode-badge">
                        {mode === "admin" ? "Mode Admin" : "Mode Supervisor"}
                    </p>
                )}
                <section className="fade-up" data-testid="rendering-section">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">Rendering Ruangan</p>
                    {room.renderings?.length > 0 ? (
                        <div className="space-y-4">
                            {room.renderings.map((path, i) => (
                                <div key={path} className="border border-border bg-white p-2 fade-up" style={{ animationDelay: `${i * 80}ms` }}>
                                    <button
                                        className="block w-full cursor-zoom-in"
                                        onClick={() => setZoomPhoto({ src: fileUrl(path), note: `${room.name} — Rendering ${i + 1}` })}
                                        data-testid={`rendering-zoom-${i}`}
                                        aria-label="Perbesar rendering"
                                    >
                                        <img
                                            src={fileUrl(path)}
                                            alt={`Rendering ${room.name} ${i + 1}`}
                                            className="w-full max-h-[70vh] object-contain"
                                            data-testid={`room-rendering-image-${i}`}
                                        />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground" data-testid="rendering-empty">
                            Rendering belum tersedia untuk ruangan ini.
                        </div>
                    )}
                    {room.description && <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{room.description}</p>}
                </section>

                {(room.me_link || (mode !== "public" && (room.gambar_kerja_link || room.extra_links?.length > 0))) && (
                    <section className="space-y-3 fade-up" style={{ animationDelay: "100ms" }} data-testid="drive-links-section">
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">Dokumen Google Drive</p>
                        {room.me_link && (
                            <DriveButton
                                testId="me-drive-link"
                                href={room.me_link}
                                icon={Lightning}
                                title="Gambar ME / Electrical"
                                subtitle="Buka folder Google Drive"
                            />
                        )}
                        {mode !== "public" && room.gambar_kerja_link && (
                            <DriveButton
                                testId="gk-drive-link"
                                href={room.gambar_kerja_link}
                                icon={FileArrowDown}
                                title={`Gambar Kerja${room.gambar_kerja_version ? ` • ${room.gambar_kerja_version}` : ""}`}
                                subtitle="Khusus internal — buka folder Google Drive"
                            />
                        )}
                        {mode !== "public" &&
                            room.extra_links?.map((l) => (
                                <DriveButton
                                    key={l.id}
                                    testId={`extra-drive-link-${l.id}`}
                                    href={l.url}
                                    icon={LinkIcon}
                                    title={l.label || "Tautan Google Drive"}
                                    subtitle="Buka folder Google Drive"
                                />
                            ))}
                    </section>
                )}

                {mode !== "public" && (
                    <section className="fade-up" style={{ animationDelay: "150ms" }} data-testid="progress-section">
                        <div className="mb-4">
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Progress</p>
                            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-medium mt-1" data-testid="progress-heading">Foto Progress Lapangan</h2>
                        </div>
                        <a
                            data-testid="progress-open-btn"
                            href={mode === "admin" ? `/a/${roomId}/progress` : `/s/${roomId}/progress`}
                            className="flex items-center gap-4 border border-border bg-white p-5 hover:border-primary hover:-translate-y-[1px] transition-all duration-200"
                        >
                            <Images size={28} weight="light" className="text-primary shrink-0" />
                            <span className="flex-1 min-w-0">
                                <span className="block font-medium text-sm sm:text-base">
                                    Foto Progress Lapangan{progressCount > 0 ? ` (${progressCount})` : ""}
                                </span>
                                <span className="block text-xs text-muted-foreground mt-0.5">Upload & lihat galeri progress — dikelompokkan per tanggal</span>
                            </span>
                            <ArrowSquareOut size={18} weight="light" className="text-muted-foreground shrink-0" />
                        </a>
                    </section>
                )}

                <section className="fade-up" style={{ animationDelay: "200ms" }} data-testid="service-section">
                    <div className="flex items-end justify-between gap-3 mb-4">
                        <div>
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Service</p>
                            <h2 className="font-serif text-2xl sm:text-3xl tracking-tight font-medium mt-1">Foto Service</h2>
                            {room.service_photos.length > 0 && (
                                <p className="text-sm text-muted-foreground mt-1" data-testid="service-pending-count">
                                    {pendingCount} belum selesai
                                </p>
                            )}
                        </div>
                        {mode === "admin" && adminVerified && room.service_photos.length > 0 && (
                            <button
                                data-testid="report-generate-btn"
                                onClick={generateReport}
                                disabled={generating}
                                className="shrink-0 border border-primary text-primary px-3 py-2 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors flex items-center gap-2 disabled:opacity-50"
                            >
                                <FilePdf size={16} weight="light" />
                                {generating ? "Membuat PDF..." : `Report PDF${selectedIds.length > 0 ? ` (${selectedIds.length})` : ""}`}
                            </button>
                        )}
                    </div>
                    {mode === "supervisor" && room.service_photos.length > 0 && (
                        <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="supervisor-report-buttons">
                            <button
                                data-testid="report-select-btn"
                                onClick={() => generateServiceReport(false)}
                                disabled={generating}
                                className="flex-1 border border-primary text-primary px-3 py-2.5 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                <FilePdf size={16} weight="light" />
                                {generating ? "Membuat PDF..." : `Print Foto Service${selectedIds.length > 0 ? ` (${selectedIds.length} dipilih)` : " (Pilih)"}`}
                            </button>
                            <button
                                data-testid="report-pending-btn"
                                onClick={() => generateServiceReport(true)}
                                disabled={generating}
                                className="flex-1 bg-primary text-primary-foreground px-3 py-2.5 text-sm font-medium hover:bg-[#9c4a2a] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                <FilePdf size={16} weight="bold" />
                                {generating ? "Membuat PDF..." : `Print Belum Dilaksanakan${pendingCount > 0 ? ` (${pendingCount})` : ""}`}
                            </button>
                        </div>
                    )}

                    <div className="border border-border bg-white p-4 mb-6">
                        <input
                            data-testid="service-note-input"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Catatan (opsional), misal: kebocoran plafon"
                            className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-primary transition-colors bg-transparent mb-3"
                        />
                        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={uploadPhoto} data-testid="service-photo-camera-input" />
                        <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="service-photo-gallery-input" />
                        <div className="flex gap-2">
                            <button
                                data-testid="service-upload-camera-btn"
                                onClick={() => fileRef.current?.click()}
                                disabled={uploading}
                                className="flex-1 bg-primary text-primary-foreground py-3 text-sm font-medium hover:bg-[#9c4a2a] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                <Camera size={18} weight="bold" />
                                {uploading ? "Mengunggah..." : "Dari Kamera"}
                            </button>
                            <button
                                data-testid="service-upload-gallery-btn"
                                onClick={() => galleryRef.current?.click()}
                                disabled={uploading}
                                className="flex-1 border border-primary text-primary py-3 text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                                <Images size={18} weight="bold" />
                                {uploading ? "Mengunggah..." : "Dari Galeri"}
                            </button>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-2">Foto otomatis dikompresi sebelum diunggah.</p>
                    </div>

                    {photoGroups.map((group) => (
                        <div key={group.key} className="mb-6" data-testid={`service-group-${group.key}`}>
                            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3">
                                {group.title} {group.photos.length > 0 && `(${group.photos.length})`}
                            </p>
                            {group.photos.length === 0 ? (
                                <p className="text-sm text-muted-foreground" data-testid={`service-empty-${group.key}`}>
                                    Belum ada foto.
                                </p>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`service-gallery-${group.key}`}>
                                    {[...group.photos]
                                        .sort((a, b) => Number(a.is_done) - Number(b.is_done) || new Date(b.created_at) - new Date(a.created_at))
                                        .map((photo, i) => (
                                        <ServicePhoto
                                            key={photo.id}
                                            photo={photo}
                                            index={i}
                                            isAdmin={isAdmin}
                                            canAfter={mode !== "public"}
                                            canSelect={mode !== "public"}
                                            canEdit={mode !== "public"}
                                            editingNote={editSvcId === photo.id}
                                            editNoteValue={editSvcNote}
                                            onEditNoteChange={setEditSvcNote}
                                            onEditNoteStart={(p) => {
                                                setEditSvcId(p.id);
                                                setEditSvcNote(p.note || "");
                                            }}
                                            onEditNoteSave={saveServiceNote}
                                            selected={selectedIds.includes(photo.id)}
                                            onSelect={toggleSelect}
                                            onToggle={togglePhoto}
                                            onDelete={deletePhoto}
                                            onAfter={uploadAfter}
                                            onZoom={(p, which) => {
                                                const src = fileUrl(which === "after" ? p.after_path : p.storage_path);
                                                setZoomIdx(galleryImages.findIndex((g) => g.src === src));
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </section>
            </main>

            <footer className="border-t border-border py-8 text-center">
                <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">INERRE interior — Sistem Informasi Ruangan</p>
            </footer>

            {zoomPhoto && (
                <Lightbox
                    src={zoomPhoto.src}
                    alt={zoomPhoto.note || "Rendering"}
                    onClose={() => setZoomPhoto(null)}
                    caption={
                        zoomPhoto.note ? (
                            <div className="bg-black/60 text-white text-sm px-4 py-2" data-testid="rendering-lightbox-caption">
                                {zoomPhoto.note}
                            </div>
                        ) : null
                    }
                />
            )}

            {zoomIdx !== null && galleryImages[zoomIdx] && (
                <Lightbox
                    src={galleryImages[zoomIdx].src}
                    alt={galleryImages[zoomIdx].note || "Foto service"}
                    onClose={() => setZoomIdx(null)}
                    onPrev={galleryImages.length > 1 ? () => setZoomIdx((i) => (i - 1 + galleryImages.length) % galleryImages.length) : null}
                    onNext={galleryImages.length > 1 ? () => setZoomIdx((i) => (i + 1) % galleryImages.length) : null}
                    caption={
                        <div className="bg-black/60 text-white px-4 py-2.5 text-center space-y-1" data-testid="service-lightbox-caption">
                            <p className="text-[11px] font-mono tracking-[0.2em] uppercase" data-testid="service-lightbox-status">
                                {galleryImages[zoomIdx].ref || "Foto"} • {galleryImages[zoomIdx].isAfter ? "After" : "Before"}
                                {galleryImages[zoomIdx].isDone ? " • Selesai" : ""}
                            </p>
                            {galleryImages[zoomIdx].note && (
                                <p className="text-sm" data-testid="service-lightbox-note">
                                    {galleryImages[zoomIdx].note}
                                </p>
                            )}
                        </div>
                    }
                />
            )}
        </div>
    );
}
